/**
 * useApiResource — the one fetch lifecycle every dashboard page shares (#704).
 * Replaces the per-page data/loading/error triple: one effect, one
 * cancellation flag, one typed error.
 */
import { useCallback, useEffect, useState } from 'react';

export interface ApiResource<T> {
  data: T | null;
  loading: boolean;
  error: Error | null;
  refetch: () => void;
}

export function useApiResource<T>(
  fetcher: () => Promise<T>,
  deps: unknown[],
  options?: { enabled?: boolean },
): ApiResource<T> {
  const enabled = options?.enabled ?? true;
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState<Error | null>(null);
  const [tick, setTick] = useState(0);

  const refetch = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }
    let canceled = false;
    setLoading(true);
    setError(null);
    fetcher()
      .then((result) => { if (!canceled) setData(result); })
      .catch((err) => {
        if (!canceled) setError(err instanceof Error ? err : new Error(String(err)));
      })
      .finally(() => { if (!canceled) setLoading(false); });
    return () => { canceled = true; };
    // `fetcher` is intentionally excluded: callers pass an inline closure whose
    // identity changes every render. `deps` is the caller's explicit contract.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, tick, enabled]);

  return { data, loading, error, refetch };
}
